// safetyPage/safetyAudit/SafetyAuditTab.js
// This will hold the SafetyAuditTable and the SafetyAuditForm for users to fill out.

import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  parseSafetyFormRoute,
  buildCanonicalSafetyFormPath,
} from "../../../../services/SafetyFormRouteUtils";
import { shallow } from "zustand/shallow";
import { useAppStore } from "../../../../store/appStore";
// User
import { useUserState } from "../../../../context/user";
// Components
import { SafetyAuditTable } from "./SafetyAuditTable";
import { SafetyAuditForm } from "../../../safetyAuditForm/SafetyAuditForm";
import {
  NewSafetyFormButton,
  EditSafetyFormButton,
  RefreshSafetyFormListsButton,
} from "../../../landingPage/components/buttons/SafetyFormActions";
// Styles
import styles from "./SafetyAuditTab.module.css";

export const SafetyAuditTab = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // User Info
  const user = useUserState().currentUser;

  // Employees
  const employees = useAppStore((s) => s.employees.list, shallow);

  // Loading UI actions
  const showLoading = useAppStore((s) => s.loadingActions.showLoading);
  const hideLoading = useAppStore((s) => s.loadingActions.hideLoading);

  // Notification UI actions
  const { showNotification } = useAppStore(
    (s) => s.notificationActions,
    shallow
  );

  // Safety Audit List State/Actions
  const safetyAuditList = useAppStore(
    (s) => s.safetyFormLists.safetyAuditList.items,
    shallow
  );
  const totalCount = useAppStore(
    (s) => s.safetyFormLists.safetyAuditList.totalCount,
    shallow
  );
  const currentPage = useAppStore(
    (s) => s.safetyFormLists.safetyAuditList.currentPage,
    shallow
  );
  const pageSize = useAppStore(
    (s) => s.safetyFormLists.safetyAuditList.pageSize,
    shallow
  );
  const isLoading = useAppStore(
    (s) => s.safetyFormLists.safetyAuditList.loading,
    shallow
  );

  // Safety Audit List Actions
  const setSafetyAuditListPage = useAppStore(
    (s) => s.safetyFormListsActions.setSafetyAuditListPage
  );
  const setSafetyAuditListPageSize = useAppStore(
    (s) => s.safetyFormListsActions.setSafetyAuditListPageSize
  );
  const fetchSafetyAuditListDraftsPage = useAppStore(
    (s) => s.safetyFormListsActions.fetchSafetyAuditListDraftsPage
  );

  // Safety Audit Form UI/State
  const showSafetyAuditForm = useAppStore(
    (s) => s.safetyPage.showSafetyAuditChecklistForm,
    shallow
  );

  const setShowSafetyAuditForm = useAppStore(
    (s) => s.safetyPageActions.setShowSafetyAuditChecklistForm
  );

  // Selected Safety Audit from table State/Actions
  const selectedSafetyAudit = useAppStore(
    (s) => s.safetyPage.selectedSafetyAuditChecklist,
    shallow
  );
  const setSelectedSafetyAudit = useAppStore(
    (s) => s.safetyPageActions.setSelectedSafetyAuditChecklist
  );

  // Safety Audit Form stuff
  // State
  const activeSafetyAudit = useAppStore(
    (s) => s.activeSafetyAuditForm,
    shallow
  );
  const activeSafetyAuditFormActions = useAppStore(
    (s) => s.activeSafetyAuditFormActions.safetyAuditFormActions,
    shallow
  );
  // Actions
  const safetyFormWorkflowActions = useAppStore(
    (s) => s.safetyFormWorkflowActions,
    shallow
  );
  const startNewSafetyAudit = useAppStore(
    (s) => s.activeSafetyAuditFormActions.startNewSafetyAudit
  );
  const fetchSafetyAudit = useAppStore(
    (s) => s.activeSafetyAuditFormActions.fetchSafetyAudit
  );
  const setSafetyAuditErrors = useAppStore(
    (s) => s.activeSafetyAuditFormActions.setSafetyAuditErrors
  );

  // Handling row select
  const handleRowSelect = (safetyAudit) => {
    setSelectedSafetyAudit(safetyAudit);
  };

  // Handling Double clicking a row
  const handleDoubleClick = async (safetyAudit) => {
    setSelectedSafetyAudit(safetyAudit);
    const target = buildCanonicalSafetyFormPath({
      tab: "safety_audit",
      safetyAuditId: safetyAudit.id,
      action: "edit",
      open: true,
    });
    navigate(target);
  };

  // Handling delete
  const handleDelete = async (safetyAuditId) => {
    showLoading("Deleting safety audit...");
    const res = await safetyFormWorkflowActions.deleteSafetyAudit(
      safetyAuditId,
      user
    );
    if (res?.ok) {
      showNotification({
        type: "success",
        title: "Safety Audit Deleted",
        message: "The safety audit has been deleted successfully.",
      });
    } else {
      showNotification({
        type: "error",
        title: "Error Deleting Safety Audit",
        message: "The safety audit has not been deleted. Please try again.",
      });
    }
    hideLoading();
  };

  // Handle creating new safety audit when button is clicked
  const handleNewSafetyAudit = async () => {
    const target = buildCanonicalSafetyFormPath({
      tab: "safety_audit",
      action: "new",
      open: true,
    });
    navigate(target);
  };

  // Handle edit button click
  const handleEditSafetyAudit = () => {
    if (!selectedSafetyAudit) return;
    const target = buildCanonicalSafetyFormPath({
      tab: "safety_audit",
      safetyAuditId: selectedSafetyAudit.id,
      action: "edit",
      open: true,
    });
    navigate(target);
  };

  // Pagination handlers
  const handlePageSizeChange = async (e) => {
    showLoading("Loading Safety Audits...");
    const newPageSize = parseInt(e.target.value, 10);
    setSafetyAuditListPageSize(newPageSize);
    await fetchSafetyAuditListDraftsPage(user.employee_id);
    hideLoading();
  };

  const handlePageChange = async (direction) => {
    showLoading("Loading Safety Audits...");
    let newPage;
    if (direction === "next") {
      newPage = currentPage + 1;
    } else {
      newPage = currentPage - 1;
    }
    setSafetyAuditListPage({ currentPage: newPage, pageSize });
    await fetchSafetyAuditListDraftsPage(user.employee_id);
    hideLoading();
  };

  // Refresh handler
  const handleRefreshSafetyAuditLists = async () => {
    showLoading("Refreshing Safety Audits...");
    setSafetyAuditListPage({ currentPage: 1, pageSize });
    await fetchSafetyAuditListDraftsPage(user.employee_id);
    hideLoading();
  };

  // Deep-link handler (single source of truth)
  const processedRef = useRef(null);
  useEffect(() => {
    const run = async () => {
      const parsed = parseSafetyFormRoute(location.pathname, location.search);
      if (parsed.tab !== "safety_audit") {
        return;
      }

      const idStr = parsed.safetyAuditId;
      const action = parsed.action || (idStr ? "edit" : null);
      const wantsOpen = !!parsed.open;
      const key = `safety_audit:${action || ""}:${idStr || ""}:${
        wantsOpen ? "1" : "0"
      }`;

      if (processedRef.current === key) {
        return;
      }
      processedRef.current = key;

      try {
        // If URL points to list view (no safety_audit_id, no action), ensure form is closed
        if (!idStr && !action) {
          if (showSafetyAuditForm) setShowSafetyAuditForm(false);
          return;
        }

        // Guard: If already open on the same safety audit in edit mode, skip re-fetch
        if (
          idStr &&
          wantsOpen &&
          action === "edit" &&
          showSafetyAuditForm &&
          activeSafetyAudit?.currentSafetyAudit?.id &&
          String(activeSafetyAudit.currentSafetyAudit.id) === String(idStr)
        ) {
          return;
        }

        if (action === "new" && wantsOpen) {
          showLoading("Starting new Safety Audit...");
          await startNewSafetyAudit(user);
          setShowSafetyAuditForm(true);

          // After creating new Safety Audit, update URL to edit mode with safety_audit_id
          // This prevents refresh from creating another new Safety Audit
          const newSafetyAuditId =
            useAppStore.getState().activeSafetyAuditForm.currentSafetyAudit?.id;
          if (newSafetyAuditId) {
            const editTarget = buildCanonicalSafetyFormPath({
              tab: "safety_audit",
              safetyAuditId: newSafetyAuditId,
              action: "edit",
              open: true,
            });
            navigate(editTarget, { replace: true });
          }
        } else if (idStr && wantsOpen && action === "edit") {
          showLoading("Loading Safety Audit...");
          const res = await fetchSafetyAudit("edit", idStr);
          if (res && res.ok) {
            setShowSafetyAuditForm(true);
          } else {
            setShowSafetyAuditForm(false);
            showNotification({
              type: "error",
              title: "Error Loading Safety Audit",
              message: "Safety Audit not found or could not be loaded.",
            });
          }
        } else if (idStr && !wantsOpen) {
          // Select the row but don't open the form
          const safetyAudit = safetyAuditList.find(
            (sa) => String(sa.id) === String(idStr)
          );
          if (safetyAudit) setSelectedSafetyAudit(safetyAudit);
          setShowSafetyAuditForm(false);
        }
      } catch (err) {
        console.error("Failed to process Safety Audit deep link", err);
      } finally {
        hideLoading();
      }
    };
    run();
  }, [
    location.pathname,
    location.search,
    startNewSafetyAudit,
    fetchSafetyAudit,
    setShowSafetyAuditForm,
    showLoading,
    hideLoading,
    user,
    safetyAuditList,
    activeSafetyAudit,
    showSafetyAuditForm,
    showNotification,
    setSelectedSafetyAudit,
  ]);

  return (
    <>
      {showSafetyAuditForm ? (
        <>
          <SafetyAuditForm
            currentSafetyAudit={activeSafetyAudit.currentSafetyAudit}
            safetyAuditMode={activeSafetyAudit.safetyAuditMode}
            safetyAuditFormUI={
              activeSafetyAudit.safetyAuditForm?.safetyAuditFormUI
            }
            jsaFormUI={activeSafetyAudit.safetyAuditForm?.jsaFormUI}
            photosUI={activeSafetyAudit.safetyAuditForm?.photosUI}
            rev={activeSafetyAudit.rev}
            safetyAuditFormActions={activeSafetyAuditFormActions}
            setShowSafetyAuditForm={setShowSafetyAuditForm}
            autosaveErrors={activeSafetyAudit.safetyAuditForm?.autosaveErrors}
            autosaveErrorsActions={activeSafetyAuditFormActions?.autosaveErrors}
            refreshListFunction={handleRefreshSafetyAuditLists}
          />
        </>
      ) : (
        <>
          <div className={styles.safetyAuditTabContainer}>
            <div className={styles.tableActions}>
              <NewSafetyFormButton
                formName="Safety Audit"
                newFormFunction={handleNewSafetyAudit}
              />
              <EditSafetyFormButton
                formName="Safety Audit"
                editFunction={handleEditSafetyAudit}
              />
            </div>

            <div className={styles.headerWithRefresh}>
              <h2>Safety Audit Forms</h2>
              <RefreshSafetyFormListsButton
                refreshFunction={handleRefreshSafetyAuditLists}
                isLoading={isLoading}
              />
            </div>

            <div className={styles.safetyAuditTableWrapper}>
              {safetyAuditList?.length > 0 ? (
                <SafetyAuditTable
                  safetyAuditList={safetyAuditList.slice(0, pageSize)}
                  onRowSelect={handleRowSelect}
                  onDoubleClick={handleDoubleClick}
                  onDelete={handleDelete}
                  selectedSafetyAuditId={selectedSafetyAudit?.id}
                />
              ) : (
                <div className={styles.noData}>No safety audits to display</div>
              )}
            </div>
            <div className={styles.paginationControls}>
              <div className={styles.paginationLeftControls}>
                <button
                  onClick={() => handlePageChange("prev")}
                  disabled={currentPage === 1 || isLoading}
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange("next")}
                  disabled={currentPage * pageSize >= totalCount || isLoading}
                >
                  Next
                </button>
                <div className={styles.pageSizeSelector}>
                  <label htmlFor="pageSize">Rows:</label>
                  <select
                    id="pageSize"
                    className={styles.pageSizeSelect}
                    value={pageSize}
                    onChange={handlePageSizeChange}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                  </select>
                </div>
              </div>
              <span>
                Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)}{" "}
                - {Math.min(currentPage * pageSize, totalCount)} of {totalCount}{" "}
                safety audits
              </span>
            </div>
          </div>
        </>
      )}
    </>
  );
};
